import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { setOrgId } from "../api/client";
import { orgs } from "../api/orgs";
import { ChevronDown } from "./icons";
import { t } from "../i18n";
import "./orgSwitcher.css";

/*
 * The organization switcher in the top bar (Zoho's organization menu). Shown once a person belongs to more than one
 * organization. Choosing one is remembered on the server, then the page reloads into it, so no screen keeps data from
 * the organization it came from.
 */
export default function OrgSwitcher({ onManage }: { onManage: () => void }) {
  const { staff } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const current = staff?.organization;
  const list = staff?.organizations ?? [];
  if (!current || list.length < 2) return null;

  async function choose(id: number) {
    if (id === current?.id) return setOpen(false);
    setBusy(true);
    setError(null);
    try {
      await orgs.switchTo(id);
      setOrgId(id);
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Could not switch."));
      setBusy(false);
    }
  }

  return (
    <div className="profile-wrap org-switcher" ref={ref}>
      <button className="org-switcher__button" aria-expanded={open} aria-haspopup="menu" aria-label={t("Organization: {name}", { name: current.name })} onClick={() => setOpen((o) => !o)}>
        <span className="org-switcher__name">{current.name}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="profile-menu org-switcher__menu" role="menu">
          <p className="org-switcher__title">{t("Organizations")}</p>
          {list.map((o) => (
            <button key={o.id} role="menuitemradio" aria-checked={o.id === current.id} disabled={busy} className={o.id === current.id ? "is-current" : undefined} onClick={() => void choose(o.id)}>
              <span>
                <strong>{o.name}</strong>
                {o.gstin && <small>{o.gstin}</small>}
              </span>
            </button>
          ))}
          {error && <p className="org-switcher__error">{error}</p>}
          <button
            className="profile-menu__signout"
            onClick={() => {
              setOpen(false);
              onManage();
            }}
          >
            {t("Manage organizations")}
          </button>
        </div>
      )}
    </div>
  );
}
